/**
 * Template builder helpers.
 *
 * In the builder, sections / items / columns / steps / bands carry a few
 * client-only fields (all starting with "_") that are stripped before saving:
 *   _id        stable React key + drag-and-drop id
 *   _saved     came from the server; its `key` can no longer change
 *   _keyEdited user typed the key, so stop generating it from the title
 */

export const SECTION_TYPES = [
  {
    value: "RATING",
    label: "Rating",
    icon: "bi-star-half",
    hint: "Items rated on a scale; counts toward the score",
  },
  {
    value: "TEXT_LIST",
    label: "Text list",
    icon: "bi-list-ol",
    hint: "e.g. Major accomplishments",
  },
  {
    value: "TABLE",
    label: "Table",
    icon: "bi-table",
    hint: "e.g. Development plan",
  },
  {
    value: "TEXT",
    label: "Free text",
    icon: "bi-text-paragraph",
    hint: "One text box",
  },
];
export const SECTION_TYPE_LABEL = Object.fromEntries(
  SECTION_TYPES.map((t) => [t.value, t.label]),
);

export const EVALUATION_TYPES = ["Internal", "Deployed", "Both"];

export const SCALE_PRESETS = {
  "1-5": {
    min: 1,
    max: 5,
    labels: [
      { value: 5, label: "Outstanding" },
      { value: 4, label: "Exceeds Expectations" },
      { value: 3, label: "Meets Expectations" },
      { value: 2, label: "Partially Meets Expectations" },
      { value: 1, label: "Did Not Meet Expectations" },
    ],
  },
  "1-3": {
    min: 1,
    max: 3,
    labels: [
      { value: 3, label: "Always Demonstrates" },
      { value: 2, label: "Sometimes Demonstrates" },
      { value: 1, label: "Never Demonstrates" },
    ],
  },
};

export const DEFAULT_BANDS = [
  { min: 90, max: 100, label: "Outstanding" },
  { min: 80, max: 89.99, label: "Exceeds Expectations" },
  { min: 70, max: 79.99, label: "Meets Expectations" },
  { min: 60, max: 69.99, label: "Partially Meets Expectations" },
  { min: 0, max: 59.99, label: "Did Not Meet Expectations" },
];

let counter = 0;
export const uid = () =>
  `tmp-${Date.now().toString(36)}-${(counter++).toString(36)}`;

/** "Part I – Key Result Areas" → "part-i-key-result-areas" (a-z 0-9 - only). */
export const slugify = (text = "") =>
  text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);

/** Valid key: lowercase letters, digits, "_" and "-". */
export const KEY_PATTERN = /^[a-z0-9_-]+$/;
export const sanitizeKey = (text = "") =>
  text.toLowerCase().replace(/[^a-z0-9_-]/g, "");

/** First free key: "kra", "kra-2", "kra-3"... */
export const uniqueKey = (base, takenKeys) => {
  const root = base || "item";
  let key = root;
  for (let n = 2; takenKeys.includes(key); n++) key = `${root}-${n}`;
  return key;
};

/**
 * New title for an element in a list: regenerate its key from the title
 * unless the key is locked (saved) or was typed by hand.
 */
export const withTitle = (list, index, title, titleField = "title") => {
  const el = list[index];
  const next = { ...el, [titleField]: title };
  if (!el._saved && !el._keyEdited) {
    const taken = list.filter((_, i) => i !== index).map((x) => x.key);
    next.key = uniqueKey(slugify(title), taken);
  }
  return next;
};

const numOr = (v, fallback = "") =>
  v === null || v === undefined ? fallback : v;
const clone = (x) => JSON.parse(JSON.stringify(x));

/* ---------- Empty shapes ---------- */
export const newItem = () => ({
  _id: uid(),
  key: "",
  title: "",
  description: "",
  kpis: [],
  weight: "",
});
export const newColumn = () => ({
  _id: uid(),
  key: "",
  label: "",
  type: "text",
});
export const newBand = (b = { min: "", max: "", label: "" }) => ({
  _id: uid(),
  ...b,
});

export const newSection = (type) => {
  const base = {
    _id: uid(),
    key: "",
    title: "",
    description: "",
    type,
    required: false,
  };
  switch (type) {
    case "RATING":
      return {
        ...base,
        weight: "",
        scale: clone(SCALE_PRESETS["1-5"]),
        itemWeighting: "EQUAL",
        allowItemComments: false,
        allowSectionComment: false,
        items: [newItem()],
      };
    case "TEXT_LIST":
      return { ...base, minEntries: "", maxEntries: 3 };
    case "TABLE":
      return { ...base, maxRows: 5, columns: [newColumn()] };
    default:
      return base;
  }
};

export const emptyTemplate = () => ({
  code: "",
  name: "",
  description: "",
  evaluationType: "Internal",
  companyId: "",
  appliesTo: { roleIds: [], jobTitleIds: [] },
  allowSelfAssessment: false,
  sections: [],
  ratingBands: DEFAULT_BANDS.map((b) => newBand(b)),
  status: "DRAFT",
});

/* ---------- Server ⇄ builder ---------- */

/** Server template → builder state (adds the client-only fields). */
export const fromServer = (t) => ({
  ...t,
  description: t.description || "",
  companyId: t.companyId || "",
  appliesTo: {
    roleIds: t.appliesTo?.roleIds || [],
    jobTitleIds: t.appliesTo?.jobTitleIds || [],
  },
  sections: (t.sections || []).map((s) => ({
    ...newSection(s.type),
    ...s,
    _id: uid(),
    _saved: true,
    description: s.description || "",
    required: !!s.required,
    weight: numOr(s.weight),
    minEntries: numOr(s.minEntries),
    maxEntries: numOr(s.maxEntries),
    maxRows: numOr(s.maxRows),
    ...(s.type === "RATING" && {
      scale: {
        min: s.scale?.min ?? 1,
        max: s.scale?.max ?? 5,
        labels: s.scale?.labels || [],
      },
      itemWeighting: s.itemWeighting || "EQUAL",
      items: (s.items || []).map((it) => ({
        ...it,
        _id: uid(),
        _saved: true,
        description: it.description || "",
        kpis: it.kpis || [],
        weight: numOr(it.weight),
      })),
    }),
    ...(s.type === "TABLE" && {
      columns: (s.columns || []).map((c) => ({
        ...c,
        _id: uid(),
        _saved: true,
        type: c.type || "text",
      })),
    }),
  })),
  ratingBands: (t.ratingBands || []).map((b) => newBand(b)),
});

/** After a successful save: every key is now locked. */
export const markSaved = (form) => ({
  ...form,
  sections: form.sections.map((s) => ({
    ...s,
    _saved: true,
    ...(s.items && { items: s.items.map((it) => ({ ...it, _saved: true })) }),
    ...(s.columns && {
      columns: s.columns.map((c) => ({ ...c, _saved: true })),
    }),
  })),
});

const toNum = (v) =>
  v === "" || v === null || v === undefined || Number.isNaN(Number(v))
    ? undefined
    : Number(v);
const text = (v) => (v && String(v).trim()) || undefined;

/** Builder state → request body (create includes `code`; PATCH can't change it). */
export const toPayload = (form, { isCreate } = {}) => ({
  ...(isCreate && { code: form.code.trim().toUpperCase() }),
  name: form.name.trim(),
  description: text(form.description) ?? null,
  evaluationType: form.evaluationType,
  companyId: form.companyId || null,
  appliesTo: {
    roleIds: form.appliesTo.roleIds,
    jobTitleIds: form.appliesTo.jobTitleIds,
  },
  allowSelfAssessment: !!form.allowSelfAssessment,
  sections: form.sections.map((s) => {
    const base = {
      key: s.key,
      title: s.title.trim(),
      description: text(s.description),
      type: s.type,
      required: !!s.required,
    };
    if (s.type === "RATING") {
      const custom = s.itemWeighting === "CUSTOM";
      return {
        ...base,
        weight: toNum(s.weight),
        scale: {
          min: toNum(s.scale.min),
          max: toNum(s.scale.max),
          labels: s.scale.labels
            .filter((l) => l.label && l.label.trim())
            .map((l) => ({ value: l.value, label: l.label.trim() })),
        },
        itemWeighting: s.itemWeighting,
        allowItemComments: !!s.allowItemComments,
        allowSectionComment: !!s.allowSectionComment,
        items: s.items.map((it) => ({
          key: it.key,
          title: it.title.trim(),
          description: text(it.description),
          kpis: it.kpis.length ? it.kpis : undefined,
          weight: custom ? toNum(it.weight) : undefined,
        })),
      };
    }
    if (s.type === "TEXT_LIST")
      return {
        ...base,
        minEntries: toNum(s.minEntries),
        maxEntries: toNum(s.maxEntries),
      };
    if (s.type === "TABLE") {
      return {
        ...base,
        maxRows: toNum(s.maxRows),
        columns: s.columns.map((c) => ({
          key: c.key,
          label: c.label.trim(),
          type: c.type === "date" ? "date" : "text",
        })),
      };
    }
    return base;
  }),
  ratingBands: form.ratingBands.map((b) => ({
    min: toNum(b.min),
    max: toNum(b.max),
    label: b.label.trim(),
  })),
});

/* ---------- Scale ---------- */

/** Labels for every value min..max (highest first), keeping labels already typed. */
export const scaleLabels = (scale) => {
  const min = Number(scale.min);
  const max = Number(scale.max);
  if (
    !Number.isInteger(min) ||
    !Number.isInteger(max) ||
    max < min ||
    max - min > 20
  )
    return scale.labels || [];
  const byValue = new Map(
    (scale.labels || []).map((l) => [Number(l.value), l.label]),
  );
  const out = [];
  for (let v = max; v >= min; v--)
    out.push({ value: v, label: byValue.get(v) || "" });
  return out;
};

/* ---------- Weights & scoring (same rules as the server) ---------- */
const round2 = (n) => Math.round(n * 100) / 100;

export const sectionWeightTotal = (sections) =>
  round2(
    sections
      .filter((s) => s.type === "RATING")
      .reduce((sum, s) => sum + (Number(s.weight) || 0), 0),
  );

export const itemWeightTotal = (section) =>
  round2(
    (section.items || []).reduce(
      (sum, it) => sum + (Number(it.weight) || 0),
      0,
    ),
  );

/** Effective weight of one item (%): equal split, or its own custom weight. */
export const itemWeight = (section, item) =>
  section.itemWeighting === "CUSTOM"
    ? Number(item.weight) || 0
    : section.items.length
      ? 100 / section.items.length
      : 0;

/**
 * Preview score.
 * item points = rating ÷ scale max × item weight
 * section score = (sum of item points) × section weight ÷ 100
 * total = sum of section scores (out of 100)
 * ratings: { [section._id]: { [item._id]: number } }
 */
export const computeScores = (sections, ratings) => {
  const rows = sections
    .filter((s) => s.type === "RATING")
    .map((s) => {
      const max = Number(s.scale?.max) || 0;
      const achieved = s.items.reduce((sum, it) => {
        const r = ratings[s._id]?.[it._id];
        return r === undefined || r === null || !max
          ? sum
          : sum + (r / max) * itemWeight(s, it);
      }, 0);
      const weight = Number(s.weight) || 0;
      return {
        id: s._id,
        title: s.title || "Untitled section",
        weight,
        achieved: round2(achieved),
        score: round2((achieved * weight) / 100),
      };
    });
  return {
    sections: rows,
    total: round2(rows.reduce((t, r) => t + r.score, 0)),
  };
};

export const bandFor = (bands, total) =>
  bands.find((b) => b.label && total >= Number(b.min) && total <= Number(b.max))
    ?.label ?? null;

/** Readable problems with the rating bands (overlaps, min > max). */
export const bandIssues = (bands) => {
  const issues = [];
  const valid = bands.filter((b) => b.min !== "" && b.max !== "");
  valid.forEach((b) => {
    if (Number(b.min) > Number(b.max))
      issues.push(`"${b.label || "Unnamed band"}": min is above max.`);
  });
  const sorted = [...valid].sort((a, b) => Number(a.min) - Number(b.min));
  for (let i = 1; i < sorted.length; i++) {
    if (Number(sorted[i].min) <= Number(sorted[i - 1].max)) {
      issues.push(
        `"${sorted[i - 1].label || "Unnamed"}" and "${sorted[i].label || "Unnamed"}" overlap.`,
      );
    }
  }
  return issues;
};
